/**
 * The tool catalogue: TradingView names and groups (docs/TRADINGVIEW-REFERENCE.md §13.1), how
 * many points each takes, the creation gesture and the factory style. The rail, the object tree,
 * the settings dialog and the engine all read this table.
 */
import {
  DEFAULT_FIB_LEVELS,
  FIB_CIRCLE_LEVELS,
  FIB_FAN_LEVELS,
  FIB_TIME_ZONE_LEVELS,
  GANN_BOX_LEVELS,
  GANN_FAN_LEVELS,
  TREND_FIB_TIME_LEVELS,
} from "./fib.ts";
import type { DrawingStyle, DrawingType, FibLevel } from "./types.ts";

/** Rail flyouts (TradingView's left toolbar; Patterns and Icons are not built). */
export type ToolGroup = "lines" | "fib" | "forecast" | "shapes" | "annotation";

export const TOOL_GROUPS: readonly { id: ToolGroup; name: string }[] = [
  { id: "lines", name: "Lines" },
  { id: "fib", name: "Fib & Gann" },
  { id: "forecast", name: "Forecasting & measurement" },
  { id: "shapes", name: "Geometric shapes" },
  { id: "annotation", name: "Annotation" },
];

/**
 * Creation gesture: `click` = one click per point (a press-drag-release also places a two-point
 * tool); `multi` = clicks until double-click / Enter (path, polyline); `freehand` = press and drag.
 */
export type ToolMode = "click" | "multi" | "freehand";

export type ToolSpec = {
  type: DrawingType;
  name: string;
  group: ToolGroup;
  /** Sub-heading inside the flyout. */
  section: string;
  /** Points placed by clicks (click mode); multi / freehand: the minimum. */
  points: number;
  mode: ToolMode;
  /** Factory style (merged over `BASE_STYLE`). */
  style: Partial<DrawingStyle>;
  /** TradingView shortcut label (the matcher is in shortcuts.ts). */
  shortcut?: string;
  /** Default text for text-like tools. */
  text?: string;
};

export const BLUE = "#2962FF";
const PURPLE = "#9C27B0";
const GREY = "#787B86";
const GREEN = "#089981";
const RED = "#F23645";
const ORANGE = "#FF9800";

export const BASE_STYLE: DrawingStyle = {
  color: BLUE,
  width: 2,
  line: "solid",
  fill: null,
  extendLeft: false,
  extendRight: false,
  textColor: BLUE,
  showPrice: false,
  showInfo: false,
};

const lv = (levels: readonly FibLevel[]): FibLevel[] => levels.map((l) => ({ ...l }));

const FIB_STYLE: Partial<DrawingStyle> = {
  color: GREY,
  width: 1,
  line: "solid",
  fibLabels: "values",
  fibPrices: true,
  reverse: false,
  trendLine: true,
  fill: "#FFFFFF26",
};

export const TOOLS: readonly ToolSpec[] = [
  // ---- Lines
  {
    type: "trend_line",
    name: "Trend line",
    group: "lines",
    section: "Lines",
    points: 2,
    mode: "click",
    style: {},
    shortcut: "Alt+T",
  },
  {
    type: "ray",
    name: "Ray",
    group: "lines",
    section: "Lines",
    points: 2,
    mode: "click",
    style: { extendRight: true },
  },
  {
    type: "info_line",
    name: "Info line",
    group: "lines",
    section: "Lines",
    points: 2,
    mode: "click",
    style: { showInfo: true },
  },
  {
    type: "extended_line",
    name: "Extended line",
    group: "lines",
    section: "Lines",
    points: 2,
    mode: "click",
    style: { extendLeft: true, extendRight: true },
  },
  {
    type: "trend_angle",
    name: "Trend angle",
    group: "lines",
    section: "Lines",
    points: 2,
    mode: "click",
    style: { showInfo: true },
  },
  {
    type: "horizontal_line",
    name: "Horizontal line",
    group: "lines",
    section: "Lines",
    points: 1,
    mode: "click",
    style: { showPrice: true, width: 1 },
    shortcut: "Alt+H",
  },
  {
    type: "horizontal_ray",
    name: "Horizontal ray",
    group: "lines",
    section: "Lines",
    points: 1,
    mode: "click",
    style: { showPrice: true, width: 1 },
    shortcut: "Alt+J",
  },
  {
    type: "vertical_line",
    name: "Vertical line",
    group: "lines",
    section: "Lines",
    points: 1,
    mode: "click",
    style: { width: 1 },
    shortcut: "Alt+V",
  },
  {
    type: "cross_line",
    name: "Cross line",
    group: "lines",
    section: "Lines",
    points: 1,
    mode: "click",
    style: { showPrice: true, width: 1 },
    shortcut: "Alt+C",
  },
  {
    type: "arrow",
    name: "Arrow",
    group: "lines",
    section: "Lines",
    points: 2,
    mode: "click",
    style: { arrowEnd: true },
  },
  {
    type: "parallel_channel",
    name: "Parallel channel",
    group: "lines",
    section: "Channels",
    points: 3,
    mode: "click",
    style: { fill: "#2962FF1F", middle: true },
  },
  {
    type: "regression_trend",
    name: "Regression trend",
    group: "lines",
    section: "Channels",
    points: 2,
    mode: "click",
    style: { fill: "#2962FF1A", deviations: 2, color: BLUE, width: 1 },
  },
  {
    type: "flat_top_bottom",
    name: "Flat top/bottom",
    group: "lines",
    section: "Channels",
    points: 3,
    mode: "click",
    style: { fill: "#FF98001F", color: ORANGE },
  },
  {
    type: "disjoint_channel",
    name: "Disjoint channel",
    group: "lines",
    section: "Channels",
    points: 3,
    mode: "click",
    style: { fill: "#0899811F", color: GREEN },
  },
  {
    type: "pitchfork",
    name: "Pitchfork",
    group: "lines",
    section: "Pitchforks",
    points: 3,
    mode: "click",
    style: { color: RED, fill: "#F236451A", middle: true },
  },
  {
    type: "schiff_pitchfork",
    name: "Schiff pitchfork",
    group: "lines",
    section: "Pitchforks",
    points: 3,
    mode: "click",
    style: { color: RED, fill: "#F236451A", middle: true },
  },
  {
    type: "modified_schiff_pitchfork",
    name: "Modified Schiff pitchfork",
    group: "lines",
    section: "Pitchforks",
    points: 3,
    mode: "click",
    style: { color: RED, fill: "#F236451A", middle: true },
  },
  {
    type: "inside_pitchfork",
    name: "Inside pitchfork",
    group: "lines",
    section: "Pitchforks",
    points: 3,
    mode: "click",
    style: { color: RED, fill: "#F236451A", middle: true },
  },
  // ---- Fib & Gann
  {
    type: "fib_retracement",
    name: "Fib retracement",
    group: "fib",
    section: "Fibonacci",
    points: 2,
    mode: "click",
    style: { ...FIB_STYLE, line: "dashed", levels: lv(DEFAULT_FIB_LEVELS) },
    shortcut: "Alt+F",
  },
  {
    type: "fib_extension",
    name: "Trend-based fib extension",
    group: "fib",
    section: "Fibonacci",
    points: 3,
    mode: "click",
    style: { ...FIB_STYLE, line: "dashed", levels: lv(DEFAULT_FIB_LEVELS) },
  },
  {
    type: "fib_channel",
    name: "Fib channel",
    group: "fib",
    section: "Fibonacci",
    points: 3,
    mode: "click",
    style: { ...FIB_STYLE, levels: lv(DEFAULT_FIB_LEVELS) },
  },
  {
    type: "fib_time_zone",
    name: "Fib time zone",
    group: "fib",
    section: "Fibonacci",
    points: 2,
    mode: "click",
    style: { ...FIB_STYLE, fill: null, fibPrices: false, levels: lv(FIB_TIME_ZONE_LEVELS) },
  },
  {
    type: "fib_speed_fan",
    name: "Fib speed resistance fan",
    group: "fib",
    section: "Fibonacci",
    points: 2,
    mode: "click",
    style: { ...FIB_STYLE, fibPrices: false, levels: lv(FIB_FAN_LEVELS) },
  },
  {
    type: "fib_trend_time",
    name: "Trend-based fib time",
    group: "fib",
    section: "Fibonacci",
    points: 3,
    mode: "click",
    style: { ...FIB_STYLE, fill: null, fibPrices: false, levels: lv(TREND_FIB_TIME_LEVELS) },
  },
  {
    type: "fib_circles",
    name: "Fib circles",
    group: "fib",
    section: "Fibonacci",
    points: 2,
    mode: "click",
    style: { ...FIB_STYLE, fill: null, fibPrices: false, levels: lv(FIB_CIRCLE_LEVELS) },
  },
  {
    type: "fib_spiral",
    name: "Fib spiral",
    group: "fib",
    section: "Fibonacci",
    points: 2,
    mode: "click",
    style: { color: RED, width: 1 },
  },
  {
    type: "gann_box",
    name: "Gann box",
    group: "fib",
    section: "Gann",
    points: 2,
    mode: "click",
    style: { ...FIB_STYLE, fibPrices: false, levels: lv(GANN_BOX_LEVELS) },
  },
  {
    type: "gann_fan",
    name: "Gann fan",
    group: "fib",
    section: "Gann",
    points: 2,
    mode: "click",
    style: { ...FIB_STYLE, fill: null, fibPrices: false, levels: lv(GANN_FAN_LEVELS) },
  },
  // ---- Forecasting & measurement
  {
    type: "long_position",
    name: "Long position",
    group: "forecast",
    section: "Projection",
    points: 1,
    mode: "click",
    style: { color: GREY, width: 1, qty: 1, showInfo: true },
  },
  {
    type: "short_position",
    name: "Short position",
    group: "forecast",
    section: "Projection",
    points: 1,
    mode: "click",
    style: { color: GREY, width: 1, qty: 1, showInfo: true },
  },
  {
    type: "price_range",
    name: "Price range",
    group: "forecast",
    section: "Measurer",
    points: 2,
    mode: "click",
    style: { fill: "#2962FF33", width: 1, showInfo: true },
  },
  {
    type: "date_range",
    name: "Date range",
    group: "forecast",
    section: "Measurer",
    points: 2,
    mode: "click",
    style: { fill: "#2962FF33", width: 1, showInfo: true },
  },
  {
    type: "date_price_range",
    name: "Date and price range",
    group: "forecast",
    section: "Measurer",
    points: 2,
    mode: "click",
    style: { fill: "#2962FF33", width: 1, showInfo: true },
  },
  // ---- Geometric shapes
  {
    type: "rectangle",
    name: "Rectangle",
    group: "shapes",
    section: "Shapes",
    points: 2,
    mode: "click",
    style: { color: PURPLE, fill: "#9C27B033", width: 1 },
    shortcut: "Alt+Shift+R",
  },
  {
    type: "rotated_rectangle",
    name: "Rotated rectangle",
    group: "shapes",
    section: "Shapes",
    points: 3,
    mode: "click",
    style: { color: PURPLE, fill: "#9C27B033", width: 1 },
  },
  {
    type: "ellipse",
    name: "Ellipse",
    group: "shapes",
    section: "Shapes",
    points: 2,
    mode: "click",
    style: { color: RED, fill: "#F2364533", width: 1 },
  },
  {
    type: "circle",
    name: "Circle",
    group: "shapes",
    section: "Shapes",
    points: 2,
    mode: "click",
    style: { color: ORANGE, fill: "#FF980033", width: 1 },
  },
  {
    type: "triangle",
    name: "Triangle",
    group: "shapes",
    section: "Shapes",
    points: 3,
    mode: "click",
    style: { color: GREEN, fill: "#08998133", width: 1 },
  },
  {
    type: "arc",
    name: "Arc",
    group: "shapes",
    section: "Shapes",
    points: 3,
    mode: "click",
    style: { color: PURPLE, fill: "#9C27B033", width: 1 },
  },
  {
    type: "path",
    name: "Path",
    group: "shapes",
    section: "Shapes",
    points: 2,
    mode: "multi",
    style: { arrowEnd: true },
  },
  {
    type: "polyline",
    name: "Polyline",
    group: "shapes",
    section: "Shapes",
    points: 2,
    mode: "multi",
    style: { fill: null },
  },
  {
    type: "brush",
    name: "Brush",
    group: "shapes",
    section: "Brushes",
    points: 2,
    mode: "freehand",
    style: { color: "#00BCD4" },
  },
  {
    type: "highlighter",
    name: "Highlighter",
    group: "shapes",
    section: "Brushes",
    points: 2,
    mode: "freehand",
    style: { color: "#FFEB3B55", width: 4 },
  },
  // ---- Annotation
  {
    type: "text",
    name: "Text",
    group: "annotation",
    section: "Text",
    points: 1,
    mode: "click",
    style: { textColor: BLUE },
    text: "Text",
  },
  {
    type: "anchored_text",
    name: "Anchored text",
    group: "annotation",
    section: "Text",
    points: 1,
    mode: "click",
    style: { textColor: BLUE },
    text: "Text",
  },
  {
    type: "note",
    name: "Note",
    group: "annotation",
    section: "Text",
    points: 1,
    mode: "click",
    style: { color: BLUE, textColor: "#FFFFFF" },
    text: "Note",
  },
  {
    type: "callout",
    name: "Callout",
    group: "annotation",
    section: "Text",
    points: 2,
    mode: "click",
    style: { color: BLUE, fill: "#2962FF", textColor: "#FFFFFF" },
    text: "Callout",
  },
  {
    type: "price_label",
    name: "Price label",
    group: "annotation",
    section: "Text",
    points: 1,
    mode: "click",
    style: { color: BLUE, fill: "#2962FF", textColor: "#FFFFFF" },
  },
  {
    type: "arrow_marker",
    name: "Arrow marker",
    group: "annotation",
    section: "Marks",
    points: 1,
    mode: "click",
    style: { color: GREEN, direction: "up" },
  },
];

const BY_TYPE = new Map<DrawingType, ToolSpec>(TOOLS.map((t) => [t.type, t]));

export function toolSpec(type: DrawingType): ToolSpec {
  const t = BY_TYPE.get(type);
  if (!t) throw new Error(`unknown drawing tool ${type}`);
  return t;
}

export function isDrawingType(v: unknown): v is DrawingType {
  return typeof v === "string" && BY_TYPE.has(v as DrawingType);
}

/** Factory style for a tool (fresh copy: levels are cloned). */
export function factoryStyle(type: DrawingType): DrawingStyle {
  const s: DrawingStyle = { ...BASE_STYLE, ...toolSpec(type).style };
  if (s.levels) s.levels = s.levels.map((l) => ({ ...l }));
  return s;
}

/** Tools with a price anchor only (x spans the chart): they need no time on placement. */
export const PRICE_ONLY: ReadonlySet<DrawingType> = new Set(["horizontal_line"]);
/** Tools anchored in time only (y spans the chart). */
export const TIME_ONLY: ReadonlySet<DrawingType> = new Set(["vertical_line", "fib_time_zone"]);
